import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Bug, Lightbulb, Question, Trash, ArrowClockwise, CheckCircle, Envelope, LinkSimple, ShieldWarning } from "@phosphor-icons/react";

const TYPE_META = {
  bug:     { label: "Bug",     icon: Bug,      cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  idea:    { label: "Idea",    icon: Lightbulb,cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  general: { label: "General", icon: Question, cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
};

const STATUS_META = {
  new:      { label: "New",      cls: "text-neutral-300 border-white/20" },
  reviewed: { label: "Reviewed", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  actioned: { label: "Shipped",  cls: "text-[#FF9764] border-[#FF5A1F]/40 bg-[#FF5A1F]/10" },
  wontfix:  { label: "Won't do", cls: "text-neutral-500 border-white/10 bg-white/[0.02]" },
};

function TypeTag({ type }) {
  const t = TYPE_META[type] || TYPE_META.general;
  const Icon = t.icon;
  return (
    <span
      data-testid={`feedback-type-tag-${type}`}
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase font-semibold ${t.cls}`}
    >
      <Icon size={10} weight="fill" />
      {t.label}
    </span>
  );
}

function StatusTag({ status, onChange }) {
  const s = STATUS_META[status] || STATUS_META.new;
  return (
    <select
      value={status || "new"}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`feedback-status-select`}
      className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase font-semibold bg-transparent focus:outline-none cursor-pointer ${s.cls}`}
    >
      {Object.entries(STATUS_META).map(([k, v]) => (
        <option key={k} value={k} className="bg-[#0F0F0F] text-white">{v.label}</option>
      ))}
    </select>
  );
}

// Filter tab counts helper — mirrors the InboxPage tier chip pattern.
const TAB_ORDER = ["all", "new", "bug", "idea", "general"];

export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState("all");

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await client.get("/admin/feedback");
      setItems(data);
    } catch {
      setLoadError(true);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user?.is_admin]);

  // Route guard — redirect non-admins after all hooks are registered. Keeping
  // hooks unconditional preserves stable hook ordering across auth resolution.
  if (user && !user.is_admin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const updateStatus = async (id, status) => {
    try {
      await client.patch(`/admin/feedback/${id}`, { status });
      setItems((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await client.delete(`/admin/feedback/${id}`);
      setItems((prev) => prev.filter((f) => f.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const counts = {
    all: items.length,
    new: items.filter((f) => f.status === "new").length,
    bug: items.filter((f) => f.feedback_type === "bug").length,
    idea: items.filter((f) => f.feedback_type === "idea").length,
    general: items.filter((f) => f.feedback_type === "general").length,
  };

  const filtered = items.filter((f) => {
    if (tab === "all") return true;
    if (tab === "new") return f.status === "new";
    return f.feedback_type === tab;
  });

  return (
    <div className="p-6 sm:p-10 max-w-6xl mx-auto" data-testid="admin-feedback-page">
      <PageHeader
        overline="ADMIN"
        title="Pilot Feedback"
        subtitle={`${items.length} total · ${counts.new} new`}
        action={
          <button
            data-testid="feedback-refresh-btn"
            onClick={load}
            className="rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border border-white/10 hover:border-white/20 text-neutral-300 hover:text-white transition-colors"
          >
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        }
      />

      {/* Filter tabs */}
      <div className="mt-6 flex items-center gap-1.5 flex-wrap" data-testid="feedback-tabs">
        {TAB_ORDER.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            data-testid={`feedback-tab-${k}`}
            aria-pressed={tab === k}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/60 ${
              tab === k
                ? "bg-white text-black border-white"
                : "bg-transparent text-neutral-300 border-white/15 hover:border-white/30"
            }`}
          >
            <span className="capitalize">{k}</span>
            <span className={`font-mono text-[10px] px-1.5 rounded-full ${tab === k ? "bg-black/20" : "bg-white/5"}`}>
              {counts[k]}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="p-8 text-center text-neutral-500 text-sm">Loading…</div>
        ) : loadError ? (
          <div
            className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-12 text-center"
            data-testid="feedback-load-error"
          >
            <ShieldWarning size={32} weight="light" className="text-rose-400 mx-auto mb-3" />
            <div className="overline mb-2 text-rose-300">ACCESS DENIED</div>
            <p className="text-neutral-400 text-sm max-w-sm mx-auto">
              We couldn't load feedback for this account. If you believe you should have
              access, contact an administrator.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="feedback-empty">
            <CheckCircle size={32} weight="light" className="text-neutral-600 mx-auto mb-3" />
            <div className="overline mb-2">EMPTY</div>
            <p className="text-neutral-500 text-sm">
              {tab === "all"
                ? "No feedback yet. When pilots send feedback, it'll appear here."
                : `No ${tab === "new" ? "new" : tab} feedback right now.`}
            </p>
          </div>
        ) : filtered.map((f) => (
          <div
            key={f.id}
            data-testid={`feedback-item-${f.id.slice(0, 8)}`}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition-colors"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <TypeTag type={f.feedback_type} />
                <StatusTag status={f.status} onChange={(s) => updateStatus(f.id, s)} />
                <span className="text-[11px] text-neutral-500 font-mono">
                  {new Date(f.created_at).toLocaleString(undefined, {
                    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                  })}
                </span>
              </div>
              <button
                onClick={() => remove(f.id)}
                data-testid={`feedback-delete-${f.id.slice(0, 8)}`}
                className="text-neutral-500 hover:text-rose-400 p-1 transition-colors"
                title="Delete"
              >
                <Trash size={14} />
              </button>
            </div>
            <div className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap mb-3">
              {f.message}
            </div>
            <div className="flex items-center justify-between gap-3 text-[11px] text-neutral-500 pt-3 border-t border-white/5">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex items-center gap-1 truncate">
                  <Envelope size={11} />
                  <a href={`mailto:${f.user_email}`} className="hover:text-[#FF5A1F] truncate">
                    {f.user_email}
                  </a>
                </span>
                {f.user_company && (
                  <span className="text-neutral-400 truncate">· {f.user_company}</span>
                )}
              </div>
              {f.page_url && (
                <span className="inline-flex items-center gap-1 font-mono truncate max-w-[40%]">
                  <LinkSimple size={11} />
                  {f.page_url}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
